import { createIntl, createIntlCache, IntlShape } from 'react-intl';
/**
 * Localized messages for Intl
 */
import { lazyThenable } from '@jokester/ts-commonutil/lib/concurrency/lazy-thenable';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { setRequestLanguage } from '@/apis';

dayjs.extend(localizedFormat);
dayjs.extend(relativeTime);
dayjs.extend(utc);

async function initDayjs(locale: string): Promise<void> {
  switch (matchLocale(locale)) {
    case 'en':
      await import('dayjs/locale/en');
      return;
    case 'zh-HK':
      await import('dayjs/locale/zh-hk')
      return
    default:
      await import('dayjs/locale/zh-cn');
      return;
  }
}

async function loadAntdLocale(locale: string) {
  // 引入 antd 的 locale
  switch (matchLocale(locale)) {
    case 'zh-CN':
      return import('antd/es/locale/zh_CN').then((_) => _.default);
    case 'zh-HK':
      return import('antd/es/locale/zh_HK').then((_) => _.default)
    case 'en':
      return import('antd/es/locale/en_US').then((_) => _.default);
  }
  return import('antd/es/locale/en_US').then((_) => _.default);
}

type Messages = typeof import('./zh-cn.json') | typeof import('./en.json') | typeof import('./zh-hk.json');

async function loadI18nLocale(locales: string[]): Promise<Messages> {
  for (const l of locales) {
    switch (matchLocale(l)) {
      case 'zh-CN':
        return import('./zh-cn.json').then((_) => _.default);
      case 'zh-HK':
        return import('./zh-hk.json').then((_) => _.default);
      case 'en':
        return import('./en.json').then((_) => _.default);
    }
  }
  return import('./zh-cn.json').then((_) => _.default);
}

/** 获取 antd 所用的 Validate Messages */
const getAntdValidateMessages = (locale: string) => {
  const typeTemplate_enUS = 'Value is not a valid ${type}';
  const enUS = {
    default: 'Validation error on field',
    required: 'Value is required',
    enum: 'Value must be one of [${enum}]',
    whitespace: 'Value cannot be empty',
    date: {
      format: 'Value is invalid for format date',
      parse: 'Value could not be parsed as date',
      invalid: 'Value is invalid date',
    },
    types: {
      string: typeTemplate_enUS,
      method: typeTemplate_enUS,
      array: typeTemplate_enUS,
      object: typeTemplate_enUS,
      number: typeTemplate_enUS,
      date: typeTemplate_enUS,
      boolean: typeTemplate_enUS,
      integer: typeTemplate_enUS,
      float: typeTemplate_enUS,
      regexp: typeTemplate_enUS,
      email: typeTemplate_enUS,
      url: typeTemplate_enUS,
      hex: typeTemplate_enUS,
    },
    string: {
      len: 'Value must be exactly ${len} characters',
      min: 'Value must be at least ${min} characters',
      max: 'Value cannot be longer than ${max} characters',
      range: 'Value must be between ${min} and ${max} characters',
    },
    number: {
      len: 'Value must equal ${len}',
      min: 'Value cannot be less than ${min}',
      max: 'Value cannot be greater than ${max}',
      range: 'Value must be between ${min} and ${max}',
    },
    array: {
      len: 'Value must be exactly ${len} in length',
      min: 'Value cannot be less than ${min} in length',
      max: 'Value cannot be greater than ${max} in length',
      range: 'Value must be between ${min} and ${max} in length',
    },
    pattern: {
      mismatch: 'Value does not match pattern ${pattern}',
    },
  };

  const typeTemplate_zhCN = '类型不是合法的 ${type}';
  const zhCN = {
    default: '格式错误',
    required: '必填',
    enum: '必须为 [${enum}] 其中之一',
    whitespace: '不可为空字符',
    date: {
      format: '格式日期无效',
      parse: '无法被解析成时间',
      invalid: '无效的时间',
    },
    types: {
      string: typeTemplate_zhCN,
      method: typeTemplate_zhCN,
      array: typeTemplate_zhCN,
      object: typeTemplate_zhCN,
      number: typeTemplate_zhCN,
      date: typeTemplate_zhCN,
      boolean: typeTemplate_zhCN,
      integer: typeTemplate_zhCN,
      float: typeTemplate_zhCN,
      regexp: typeTemplate_zhCN,
      email: typeTemplate_zhCN,
      url: typeTemplate_zhCN,
      hex: typeTemplate_zhCN,
    },
    string: {
      len: '长度必须为 ${len} 个字符',
      min: '长度最少为 ${min} 个字符',
      max: '长度最多为 ${max} 个字符',
      range: '长度必须在 ${min} 到 ${max} 个字符之间',
    },
    number: {
      len: '数值位数必须等于 ${len}',
      min: '数值不可小于 ${min}',
      max: '数值不可大于 ${max}',
      range: '数值必须在 ${min} 到 ${max} 之间',
    },
    array: {
      len: '元素个数必须等于 ${len}',
      min: '元素个数不可小于 ${min}',
      max: '元素个数不可大于 ${max}',
      range: '元素个数必须在 ${min} 到 ${max} 之间',
    },
    pattern: {
      mismatch: '不匹配正则：${pattern}',
    },
  };
  const typeTemplate_zhHK= '不是 ${type} 類型';
  const zhHK = {
    default: '格式錯誤',
    required: '必寫',
    enum: '必須是 [${enum}] 中的一項',
    whitespace: '不能留空',
    date: {
      format: '日期格式無效',
      parse: '錯誤的時間格式',
      invalid: '時間格式無效',
    },
    types: {
      string: typeTemplate_zhHK,
      method: typeTemplate_zhHK,
      array: typeTemplate_zhHK,
      object: typeTemplate_zhHK,
      number: typeTemplate_zhHK,
      date: typeTemplate_zhHK,
      boolean: typeTemplate_zhHK,
      integer: typeTemplate_zhHK,
      float: typeTemplate_zhHK,
      regexp: typeTemplate_zhHK,
      email: typeTemplate_zhHK,
      url: typeTemplate_zhHK,
      hex: typeTemplate_zhHK,
    },
    string: {
      len: '須填足夠 ${len} 個字符',
      min: '須填至少 ${min} 個字符',
      max: '不可超出 ${max} 個字符',
      range: '字符數須介於 ${min} 及 ${max} 之間',
    },
    number: {
      len: '須填足 ${len} 位數字',
      min: '數字不可小於 ${min}',
      max: '數字不可大於 ${max}',
      range: '數字須介於 ${min} 及 ${max} 之間',
    },
    array: {
      len: '須填足 ${len} 項',
      min: '須填至少 ${min} 項',
      max: '不可超出 ${max} 項',
      range: '總項數須介於 ${min} 及 ${max} 之間',
    },
    pattern: {
      mismatch: '格式不符合：${pattern}',
    },
  }

  switch (matchLocale(locale)) {
    case 'zh-HK':
      return zhHK;
    default:
      return zhCN;
  }
};

/** 用于在 React 组件外部使用 Intl */
const cache = createIntlCache();

let singletonIntl: IntlShape = null!;
/**
 * get a global intl instance
 * @deprecated intl should be DI-ed via React component tree
 */
export const getIntl = () => {
  // note this will not be NPE: it gets called after initI18n and mount of root component
  return singletonIntl;
};

export const availableLocales = {
  en: 'English',
  'zh-CN': '简体中文',
  'zh-HK': '繁體中文'
} as const;

export type SupportedLocale = keyof typeof availableLocales;

function matchLocale(l: string): SupportedLocale {
  switch(true) {
    case (l.startsWith('en-')):
    case (l === 'en'):
      return 'en'
    case (l.startsWith('zh-H')):
    case (l.startsWith('zh-T')):
      return 'zh-HK'
    default:
      return 'zh-CN'
  }
}

const _STORAGE_KEY_LOCALE = '_override_locale';

async function doInitI18n(locale: string, locales: string[]) {
  /*
   * TODO: ensure all locale stuff is DI-able via React tree (Store or Context)
   */
  /** get 1st preference locale from navigator */
  const [intlMessages, _dayjs, antdLocale] = await Promise.all([
    loadI18nLocale(locales),
    initDayjs(locale),
    loadAntdLocale(locale),
  ]);
  singletonIntl = createIntl(
    {
      locale,
      messages: intlMessages,
    },
    cache,
  );
  return {
    locale,
    intlMessages,
    antdLocale: antdLocale,
    antdValidateMessages: getAntdValidateMessages(locale),
  } as const;
}

export function setLocale(l: string) {
  if (l in availableLocales) {
    localStorage.setItem(_STORAGE_KEY_LOCALE, l);
    location.reload();
  } else {
    throw new Error(`unsupported locale: ${l}`);
  }
}

export const initI18n = lazyThenable(() => {
  const _savedPref = localStorage.getItem(_STORAGE_KEY_LOCALE);
  const savedPref =
    _savedPref && _savedPref in availableLocales ? _savedPref : null;
  const locale = savedPref || navigator.language;
  setRequestLanguage(matchLocale(locale));
  const locales = [savedPref, ...navigator.languages].filter(
    Boolean,
  ) as string[];
  return doInitI18n(locale, locales);
});
